import axios, { type AxiosInstance, type InternalAxiosRequestConfig } from "axios";

const TOKEN_STORAGE_KEY = "url_sensor.token";

export const api: AxiosInstance = axios.create({
  baseURL: "/api",
  headers: {
    "Content-Type": "application/json",
  },
});

api.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token =
    typeof window !== "undefined"
      ? window.localStorage.getItem(TOKEN_STORAGE_KEY)
      : null;
  if (token) {
    config.headers = config.headers ?? {};
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    if (
      error?.response?.status === 401 &&
      typeof window !== "undefined" &&
      window.location.pathname !== "/login"
    ) {
      window.localStorage.removeItem(TOKEN_STORAGE_KEY);
      window.localStorage.removeItem("url_sensor.user");
      window.location.assign("/login");
    }
    return Promise.reject(error);
  },
);

export function setAuthToken(token: string | null): void {
  if (typeof window === "undefined") return;
  if (token) {
    window.localStorage.setItem(TOKEN_STORAGE_KEY, token);
  } else {
    window.localStorage.removeItem(TOKEN_STORAGE_KEY);
  }
}

export function getAuthToken(): string | null {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem(TOKEN_STORAGE_KEY);
}

// =================== Types ===================

export type UserRole = "admin" | "operator" | "viewer";
export type ScheduleKind = "interval" | "cron";
export type IncidentSeverity = "info" | "warning" | "critical";
export type ChannelKind = "telegram" | "email";
export type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE" | "HEAD";
export type IncidentStatusFilter = "open" | "resolved" | "all";
export type UptimeRange = "24h" | "7d" | "30d";

export interface User {
  id: number;
  email: string;
  role: UserRole;
  is_active: boolean;
  created_at: string;
}

export interface Token {
  access_token: string;
  token_type: string;
  expires_in: number;
}

export interface Site {
  id: number;
  name: string;
  description: string | null;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  urls_count?: number;
  open_incidents_count?: number;
}

export interface UrlEntry {
  id: number;
  site_id: number;
  url: string;
  method: HttpMethod;
  expected_status: number;
  content_match: string | null;
  content_regex: string | null;
  headers: Record<string, string>;
  body: string | null;
  basic_auth_user: string | null;
  schedule_kind: ScheduleKind;
  schedule_value: string;
  timeout_seconds: number;
  latency_sla_ms: number | null;
  retry_count: number;
  is_active: boolean;
  check_ssl: boolean;
  created_at: string;
  updated_at: string;
  latest_check?: CheckRun | null;
  open_incident?: Incident | null;
}

export interface CheckRun {
  id: number;
  url_entry_id: number;
  started_at: string;
  finished_at: string;
  status_code: number | null;
  latency_ms: number | null;
  ok: boolean;
  error: string | null;
  content_matched: boolean | null;
  ssl_days_left: number | null;
}

export interface TelegramChannelConfig {
  bot_token?: string;
  chat_id: string;
}

export interface EmailChannelConfig {
  smtp_host?: string;
  smtp_port?: number;
  username?: string;
  password?: string;
  from_addr?: string;
  to_addrs: string[];
  cc_addrs?: string[];
}

export type ChannelConfig = TelegramChannelConfig | EmailChannelConfig;

export interface NotificationChannel {
  id: number;
  name: string;
  kind: ChannelKind;
  config: ChannelConfig;
  is_active: boolean;
}

export interface SiteChannelBinding {
  id: number;
  site_id: number;
  channel_id: number;
  min_severity: IncidentSeverity;
  channel?: NotificationChannel;
}

export interface DeliveryLog {
  id: number;
  channel_id: number | null;
  channel_name: string | null;
  kind: string | null;
  event: string | null;
  severity: string | null;
  ok: boolean;
  error: string | null;
  incident_id: number | null;
  created_at: string;
}

export interface Incident {
  id: number;
  url_entry_id: number;
  opened_at: string;
  resolved_at: string | null;
  severity: IncidentSeverity;
  last_error: string | null;
  acknowledged_by_id: number | null;
  acknowledged_at: string | null;
  site_id?: number;
  site_name?: string;
  url?: string;
}

export interface MaintenanceWindow {
  id: number;
  site_id: number | null;
  url_entry_id: number | null;
  starts_at: string;
  ends_at: string;
  reason: string | null;
}

export interface OverviewStats {
  totals: {
    sites: number;
    urls: number;
    open_incidents: number;
  };
  uptime_24h: number;
  latest_incidents: Incident[];
  channels_health?: Array<{ id: number; name: string; ok: boolean }>;
  recent_checks?: CheckRun[];
}

export interface UptimeStats {
  uptime: number;
  total_checks: number;
  ok_checks: number;
  range: UptimeRange;
}

export interface Paginated<T> {
  items: T[];
  total: number;
  page: number;
  page_size: number;
}

// =================== Auth ===================

export async function postLogin(
  email: string,
  password: string,
): Promise<Token> {
  const form = new URLSearchParams();
  form.append("username", email);
  form.append("password", password);
  const resp = await api.post<Token>("/auth/login", form, {
    headers: { "Content-Type": "application/x-www-form-urlencoded" },
  });
  return resp.data;
}

export async function getMe(): Promise<User> {
  const resp = await api.get<User>("/auth/me");
  return resp.data;
}

// =================== Sites ===================

export interface SitesListParams {
  is_active?: boolean;
  q?: string;
}

export async function listSites(params?: SitesListParams): Promise<Site[]> {
  const resp = await api.get<Site[] | Paginated<Site>>("/sites", { params });
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function getSite(id: number): Promise<Site> {
  const resp = await api.get<Site>(`/sites/${id}`);
  return resp.data;
}

export interface SiteCreate {
  name: string;
  description?: string | null;
  is_active?: boolean;
}

export type SiteUpdate = Partial<SiteCreate>;

export async function createSite(body: SiteCreate): Promise<Site> {
  const resp = await api.post<Site>("/sites", body);
  return resp.data;
}

export async function updateSite(id: number, body: SiteUpdate): Promise<Site> {
  const resp = await api.patch<Site>(`/sites/${id}`, body);
  return resp.data;
}

export async function deleteSite(id: number): Promise<void> {
  await api.delete(`/sites/${id}`);
}

// =================== URL Entries ===================

export interface UrlsListParams {
  is_active?: boolean;
  q?: string;
  page?: number;
  page_size?: number;
}

export async function listUrls(
  siteId: number,
  params?: UrlsListParams,
): Promise<UrlEntry[]> {
  const resp = await api.get<UrlEntry[] | Paginated<UrlEntry>>(
    `/sites/${siteId}/urls`,
    { params },
  );
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function listUrlsPage(
  siteId: number,
  params?: UrlsListParams,
): Promise<Paginated<UrlEntry>> {
  const resp = await api.get<UrlEntry[] | Paginated<UrlEntry>>(
    `/sites/${siteId}/urls`,
    { params },
  );
  // Server normally returns a Paginated envelope; tolerate a bare array too.
  if (Array.isArray(resp.data)) {
    return {
      items: resp.data,
      total: resp.data.length,
      page: params?.page ?? 1,
      page_size: params?.page_size ?? resp.data.length,
    };
  }
  return resp.data;
}

export async function getUrl(id: number): Promise<UrlEntry> {
  const resp = await api.get<UrlEntry>(`/urls/${id}`);
  return resp.data;
}

export interface UrlEntryCreate {
  url: string;
  method?: HttpMethod;
  expected_status?: number;
  content_match?: string | null;
  content_regex?: string | null;
  headers?: Record<string, string>;
  body?: string | null;
  basic_auth_user?: string | null;
  basic_auth_pass?: string | null;
  schedule_kind: ScheduleKind;
  schedule_value: string;
  timeout_seconds?: number;
  latency_sla_ms?: number | null;
  retry_count?: number;
  is_active?: boolean;
  check_ssl?: boolean;
}

export type UrlEntryUpdate = Partial<UrlEntryCreate>;

export async function createUrl(
  siteId: number,
  body: UrlEntryCreate,
): Promise<UrlEntry> {
  const resp = await api.post<UrlEntry>(`/sites/${siteId}/urls`, body);
  return resp.data;
}

export interface UrlEntryBulkDefaults {
  method?: HttpMethod;
  expected_status?: number;
  content_match?: string | null;
  headers?: Record<string, string>;
  body?: string | null;
  schedule_kind: ScheduleKind;
  schedule_value: string;
  timeout_seconds?: number;
  latency_sla_ms?: number | null;
  retry_count?: number;
  is_active?: boolean;
  check_ssl?: boolean;
}

export interface UrlEntryBulkCreate {
  urls: string[];
  defaults: UrlEntryBulkDefaults;
}

export interface UrlEntryBulkError {
  url: string;
  error: string;
}

export interface UrlEntryBulkResult {
  created: UrlEntry[];
  errors: UrlEntryBulkError[];
}

export async function bulkCreateUrls(
  siteId: number,
  body: UrlEntryBulkCreate,
): Promise<UrlEntryBulkResult> {
  const resp = await api.post<UrlEntryBulkResult>(
    `/sites/${siteId}/urls/bulk`,
    body,
  );
  return resp.data;
}

export async function updateUrl(
  id: number,
  body: UrlEntryUpdate,
): Promise<UrlEntry> {
  const resp = await api.patch<UrlEntry>(`/urls/${id}`, body);
  return resp.data;
}

export async function deleteUrl(id: number): Promise<void> {
  await api.delete(`/urls/${id}`);
}

export async function runUrl(id: number): Promise<CheckRun> {
  const resp = await api.post<CheckRun>(`/urls/${id}/run`);
  return resp.data;
}

// =================== Notification Channels ===================

export interface ChannelCreate {
  name: string;
  kind: ChannelKind;
  config: ChannelConfig;
  is_active?: boolean;
}

export type ChannelUpdate = Partial<ChannelCreate>;

export async function listChannels(): Promise<NotificationChannel[]> {
  const resp = await api.get<NotificationChannel[] | Paginated<NotificationChannel>>(
    "/channels",
  );
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function getChannel(id: number): Promise<NotificationChannel> {
  const resp = await api.get<NotificationChannel>(`/channels/${id}`);
  return resp.data;
}

export async function createChannel(
  body: ChannelCreate,
): Promise<NotificationChannel> {
  const resp = await api.post<NotificationChannel>("/channels", body);
  return resp.data;
}

export async function updateChannel(
  id: number,
  body: ChannelUpdate,
): Promise<NotificationChannel> {
  const resp = await api.patch<NotificationChannel>(`/channels/${id}`, body);
  return resp.data;
}

export async function deleteChannel(id: number): Promise<void> {
  await api.delete(`/channels/${id}`);
}

export async function getChannelDeliveries(params?: {
  channel_id?: number;
  only_failures?: boolean;
  limit?: number;
}): Promise<DeliveryLog[]> {
  const resp = await api.get<DeliveryLog[]>("/channels/deliveries", {
    params,
  });
  return resp.data;
}

export async function testChannel(
  id: number,
): Promise<{ ok: boolean; detail?: string }> {
  const resp = await api.post<{ ok: boolean; detail?: string }>(
    `/channels/${id}/test`,
  );
  return resp.data;
}

// =================== Site ↔ Channel Bindings ===================

export async function listSiteChannels(
  siteId: number,
): Promise<SiteChannelBinding[]> {
  const resp = await api.get<SiteChannelBinding[]>(
    `/sites/${siteId}/channels`,
  );
  return resp.data;
}

export async function bindSiteChannel(
  siteId: number,
  channelId: number,
  body?: { min_severity?: IncidentSeverity },
): Promise<SiteChannelBinding> {
  const resp = await api.post<SiteChannelBinding>(
    `/sites/${siteId}/channels/${channelId}`,
    body ?? {},
  );
  return resp.data;
}

export async function unbindSiteChannel(
  siteId: number,
  channelId: number,
): Promise<void> {
  await api.delete(`/sites/${siteId}/channels/${channelId}`);
}

// =================== Incidents ===================

export interface IncidentsListParams {
  status?: IncidentStatusFilter;
  site_id?: number;
  url_entry_id?: number;
  severity?: IncidentSeverity;
  from?: string;
  to?: string;
  page?: number;
  page_size?: number;
}

export async function listIncidents(
  params?: IncidentsListParams,
): Promise<Incident[]> {
  const resp = await api.get<Incident[] | Paginated<Incident>>("/incidents", {
    params,
  });
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function ackIncident(id: number): Promise<Incident> {
  const resp = await api.post<Incident>(`/incidents/${id}/ack`);
  return resp.data;
}

export async function resolveIncident(id: number): Promise<Incident> {
  const resp = await api.post<Incident>(`/incidents/${id}/resolve`);
  return resp.data;
}

// =================== Maintenance Windows ===================

export interface MaintenanceListParams {
  site_id?: number;
  url_entry_id?: number;
  active_now?: boolean;
  from?: string;
  to?: string;
}

export interface MaintenanceCreate {
  site_id?: number | null;
  url_entry_id?: number | null;
  starts_at: string;
  ends_at: string;
  reason?: string | null;
}

export type MaintenanceUpdate = Partial<MaintenanceCreate>;

export async function listMaintenance(
  params?: MaintenanceListParams,
): Promise<MaintenanceWindow[]> {
  const resp = await api.get<MaintenanceWindow[] | Paginated<MaintenanceWindow>>(
    "/maintenance",
    { params },
  );
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function getMaintenance(id: number): Promise<MaintenanceWindow> {
  const resp = await api.get<MaintenanceWindow>(`/maintenance/${id}`);
  return resp.data;
}

export async function createMaintenance(
  body: MaintenanceCreate,
): Promise<MaintenanceWindow> {
  const resp = await api.post<MaintenanceWindow>("/maintenance", body);
  return resp.data;
}

export async function updateMaintenance(
  id: number,
  body: MaintenanceUpdate,
): Promise<MaintenanceWindow> {
  const resp = await api.patch<MaintenanceWindow>(`/maintenance/${id}`, body);
  return resp.data;
}

export async function deleteMaintenance(id: number): Promise<void> {
  await api.delete(`/maintenance/${id}`);
}

// =================== Stats ===================

export interface UptimeParams {
  site_id?: number;
  url_entry_id?: number;
  range: UptimeRange;
}

export async function getUptime(params: UptimeParams): Promise<UptimeStats> {
  const resp = await api.get<UptimeStats>("/stats/uptime", { params });
  return resp.data;
}

export async function getRecentChecks(
  urlEntryId: number,
  limit = 50,
): Promise<CheckRun[]> {
  const resp = await api.get<CheckRun[]>("/stats/recent-checks", {
    params: { url_entry_id: urlEntryId, limit },
  });
  return resp.data;
}

export async function getOverview(): Promise<OverviewStats> {
  const resp = await api.get<OverviewStats>("/stats/overview");
  return resp.data;
}

// =================== Users ===================

export interface UsersListParams {
  role?: UserRole;
  is_active?: boolean;
}

export interface UserCreate {
  email: string;
  password: string;
  role: UserRole;
  is_active?: boolean;
}

export type UserUpdate = Partial<{
  email: string;
  password: string;
  role: UserRole;
  is_active: boolean;
}>;

export async function listUsers(params?: UsersListParams): Promise<User[]> {
  const resp = await api.get<User[] | Paginated<User>>("/users", { params });
  return Array.isArray(resp.data) ? resp.data : resp.data.items;
}

export async function getUser(id: number): Promise<User> {
  const resp = await api.get<User>(`/users/${id}`);
  return resp.data;
}

export async function createUser(body: UserCreate): Promise<User> {
  const resp = await api.post<User>("/users", body);
  return resp.data;
}

export async function updateUser(id: number, body: UserUpdate): Promise<User> {
  const resp = await api.patch<User>(`/users/${id}`, body);
  return resp.data;
}

export async function deleteUser(id: number): Promise<void> {
  await api.delete(`/users/${id}`);
}
